import React from 'react';
import { ShoppingBag, Plus, Trash2, Percent } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент секции с множественными товарами/услугами для расчета НДС
 */
const MultipleItemsSection = ({ 
  formData,
  errors,
  items,
  handleItemChange,
  addItem,
  removeItem,
  vatRates
}) => {
  // Функция для обработки изменений в полях элементов
  const handleChange = (id, field, event) => {
    const value = event.target.value;
    handleItemChange(id, field, value);
  };

  return (
    <div className="mb-6 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
          <ShoppingBag className="w-5 h-5 mr-2 text-green-500" />
          Детализация по товарам/услугам
        </h3>
        <Button
          size="sm"
          onClick={addItem}
          icon={<Plus className="w-4 h-4" />}
          variant="success"
        >
          Добавить позицию
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
          <p className="text-sm">Нет добавленных позиций. Нажмите "Добавить позицию" для начала расчета.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item, index) => {
            const itemErrors = errors.items && errors.items[item.id] ? errors.items[item.id] : {};
            
            return (
              <div 
                key={item.id} 
                className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 relative"
              >
                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                  aria-label="Удалить позицию"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Наименование товара/услуги */}
                  <div className="md:col-span-2">
                    <label htmlFor={`description-${item.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Наименование
                    </label>
                    <input
                      type="text"
                      id={`description-${item.id}`}
                      value={item.description}
                      onChange={(e) => handleChange(item.id, 'description', e)}
                      className={`form-input w-full ${itemErrors.description ? 'input-error' : ''}`}
                      placeholder="Введите наименование товара или услуги"
                    />
                    {itemErrors.description && (
                      <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                        {itemErrors.description}
                      </p>
                    )}
                  </div>
                  
                  {/* Ставка НДС */}
                  <div>
                    <label htmlFor={`taxRate-${item.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                      <Percent className="w-4 h-4 mr-1 text-indigo-500" />
                      Ставка НДС
                    </label>
                    <select
                      id={`taxRate-${item.id}`}
                      value={item.taxRate}
                      onChange={(e) => handleChange(item.id, 'taxRate', e)}
                      className="form-select w-full"
                    >
                      {formData.calculationType === 'add' ? (
                        // Ставки для начисления НДС
                        <>
                          <option value="20">20% (основная)</option>
                          <option value="10">10% (льготная)</option>
                          <option value="0">0% (экспорт)</option>
                          <option value="exempt">Без НДС</option>
                        </>
                      ) : (
                        // Ставки для выделения НДС из суммы
                        <>
                          <option value="20/120">20/120 (из 20%)</option>
                          <option value="10/110">10/110 (из 10%)</option>
                          <option value="exempt">Без НДС</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
                
                {/* Сумма */}
                <div className="mt-4">
                  <label htmlFor={`amount-${item.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {formData.calculationType === 'add' ? 'Сумма без НДС' : 'Сумма с НДС'}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id={`amount-${item.id}`}
                      value={item.amount}
                      onChange={(e) => handleChange(item.id, 'amount', e)}
                      className={`form-input w-full pl-7 ${itemErrors.amount ? 'input-error' : ''}`}
                      placeholder="Введите сумму"
                    />
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <span className="text-gray-500 dark:text-gray-400">₽</span>
                    </div>
                  </div>
                  {itemErrors.amount && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {itemErrors.amount}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {items.length > 0 && (
        <div className="mt-4 text-right">
          <Button
            size="sm"
            onClick={addItem}
            icon={<Plus className="w-4 h-4" />}
            variant="success"
          >
            Добавить ещё позицию
          </Button>
        </div>
      )}
      
      {items.length > 0 && (
        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-300">
          <p>
            При расчете с несколькими позициями, значение в поле "Сумма" игнорируется.
            Будет произведен расчет по каждой позиции отдельно.
          </p>
        </div>
      )}
    </div>
  );
};

export default MultipleItemsSection;